<template>
	<div
		ref="data-table-wrapper"
		class="flow-data-table flow-add-scrollbar"
		:class="['bar-height-' + scrollBarSize, 'bar-width-' + scrollBarSize]"
		:style="{ maxHeight: maxHeight }"
	>
		<table
			ref="data-table"
			@mousemove="handleMouseMove($event)"
			@mouseup="handleMouseUp($event)"
			@mouseleave="handleMouseUp($event)"
		>
			<thead>
				<tr @mouseleave="highlightBorderColumnIdx = -1">
					<th
						v-for="(column, c) in columns"
						:key="column.title"
						nowrap
						:class="{
							sticky: stickyColumn === column.title,
							'highlight-resize': highlightBorderColumnIdx === c
						}"
						:style="{ width: column.width ? column.width : 'auto' }"
						:selected="selectedColumn === c"
						@click="handleColumnClick(0, c)"
						@mousedown="handleMouseDown($event, highlightBorderColumnIdx)"
						@dblclick="resetWidth($event)"
					>
						<div class="display-flex align-items-center flex-gap-12 th-content">
							<div
								class="th-resizer"
								@mouseover="highlightBorderColumnIdx = c - 1"
								@mouseleave="highlightBorderColumnIdx = resizeMeta.pressed ? c - 1 : -1"
							/>
							<slot
								name="header"
								:column="column"
								:columnIndex="c"
								:handleColumnChange="handleColumnChange"
							>
								<Icon
									:name="column.icon.name"
									:size="column.icon.size"
									:state="column.icon.state"
									:type="column.icon.type"
								/><WYSIWYG
									:value.sync="column.title"
									:highlight="true"
									:highlight-state="column.hasError ? 'error' : 'primary'"
									:editable="editColumns && column.editable"
									text-classes="paragraph-2 fc-light text-nowrap"
									@keyup="
										column.hasError = !column.validator(arguments[1]);
										handleColumnChange(column.title, arguments[1], column.hasError, column);
									"
									@change="handleColumnChange(column.title, arguments[0], column.hasError, column)"
									@click.stop=""
								/>
							</slot>
							<div
								v-if="column.sortable"
								class="display-flex flex-d-c margin-lt-auto j-c-c align-items-center sort-icon"
								@click.stop="sortColumn(column.title, innerOrder === 'asc' ? 'desc' : 'asc')"
							>
								<Icon
									name="i-triangle-up"
									size="xx-small"
									type="filled"
									:effects="false"
									:state="
										innerSort === column.title && innerOrder === 'asc' ? 'primary' : 'font-light'
									"
									class="cursor-pointer"
								/>
								<Icon
									name="i-triangle-down"
									size="xx-small"
									type="filled"
									:effects="false"
									:state="
										innerSort === column.title && innerOrder === 'desc' ? 'primary' : 'font-light'
									"
									class="cursor-pointer"
								/>
							</div>
							<div
								class="th-resizer"
								@mouseover="highlightBorderColumnIdx = c"
								@mouseleave="highlightBorderColumnIdx = resizeMeta.pressed ? c : -1"
							/>
						</div>
					</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="(row, index) in rows" :key="index" @click="handleRowClick(index, row)">
					<td
						v-for="(cell, cid) in row"
						:key="cid"
						:class="{
							sticky: cid == stickyColumnIndex,
							'highlight-resize': highlightBorderColumnIdx === cid
						}"
						:selected="selectedColumn === cid"
						@click="handleColumnClick(index, cid)"
					>
						<!-- @slot Used to display custom column data with any additional components.It has scoped props {column,columnIndex,row,rowIndex} -->
						<slot name="column" :column="cell" :columnIndex="cid" :row="row" :rowIndex="index">
							<div class="padding-12 display-flex align-items-center">
								{{ cell ? cell : "" }}
								<span class="fc-light">{{ cell === null ? "null" : "" }}</span>
								<span class="fc-light">{{ cell === undefined ? "undefined" : "" }}</span>
							</div>
						</slot>
					</td>
				</tr>
			</tbody>
		</table>
		<div v-if="rows && rows.length === 0 && !loading" class="flow-data-table-empty">
			<slot name="empty">
				<EmptyState :hide-icon="true">
					<h4 class="fc-light">No data to display</h4>
				</EmptyState>
			</slot>
		</div>
		<div v-if="loading" class="flow-data-table-loading">
			<slot name="loading">
				<Loader />
			</slot>
		</div>
		<div v-if="noMoreResults && !loading && endOfTableMsg" class="flow-data-table-no-more-results">
			<p class="paragraph-1 fc-dark">End of the table!</p>
		</div>
		<div
			v-if="!noMoreResults && !loadingMore && rows.length !== 0"
			class="flow-data-table-no-more-results cursor-pointer"
		>
			<Button state="outlined" size="small" @click="loadMore(true)"> Load more data </Button>
		</div>
		<div v-if="loadingMore && !noMoreResults" class="flow-data-table-loading-more-results">
			<span class="paragraph-1 fc-primary">Loading more data</span>
			<Icon name="loading" :effects="false" :state="'primary'" />
		</div>
	</div>
</template>
<script>
import { defineComponent } from "vue";
import Icon from "./../Icon/Icon";
import WYSIWYG from "./../FormElements/WYSIWYG";
import DataTableColumn from "./DataTableColumn";
import Button from "../Button/Button";
import EmptyState from "../EmptyState/EmptyState";
import Loader from "../Loader/Loader";

export default defineComponent({
	name: "DataTable",
	components: {
		Icon,
		WYSIWYG,
		Button,
		EmptyState,
		Loader
	},
	props: {
		/**
		 * array of array [[...],[...],...]
		 */
		tableData: {
			type: Array,
			default: () => []
		},
		/**
		 * array of 'DataTableColumn'
		 */
		columns: {
			type: Array,
			default: () => [],
			validator(cls) {
				return cls.every(c => c instanceof DataTableColumn);
			}
		},
		/**
		 * max height of table
		 */
		maxHeight: {
			type: String,
			default: "900px"
		},
		/**
		 * enable edit mode
		 */
		editColumns: {
			type: Boolean,
			default: false
		},
		/**
		 * sticky column name
		 */
		stickyColumn: {
			type: String,
			default: ""
		},
		/**
		 * column title to sort
		 */
		sort: {
			type: String,
			default: ""
		},
		/**
		 * sorting order 'asc' | 'desc'
		 */
		order: {
			type: String,
			default: "asc"
		},
		/**
		 * true | false <br/> To enable pagination
		 */
		pagination: {
			type: Boolean,
			default: true
		},
		/**
		 * max results per page while paginating
		 */
		resultsPerPage: {
			type: Number,
			default: 100
		},
		/**
		 * static : It will paginate in available 'tableData' <br/>
		 * api : It will paginate based on api , that is on next event we will have to call api and update 'tableData'.
		 */
		paginationType: {
			type: String,
			default: "static",
			validator(value) {
				return ["static", "api"].includes(value);
			}
		},
		/**
		 * true | false <br/> set to true if all results are displayed from api
		 */
		noMoreApiResults: {
			type: Boolean,
			default: false
		},
		/**
		 * 2 | 4 | 6 | 8
		 */
		scrollBarSize: {
			type: Number,
			default: 4,
			validator(value) {
				return [2, 4, 6, 8].includes(value);
			}
		},
		/**
		 * true | false
		 */
		loading: {
			type: Boolean,
			default: false
		},
		/**
		 * true | false
		 */
		endOfTableMsg: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return {
			innerSort: "",
			innerOrder: "",
			offset: 0,
			loadingMore: false,
			scrollTop: 0,
			selectedColumn: -1,
			resizeMeta: {},
			highlightBorderColumnIdx: -1,
			disableClickAfterResize: false
		};
	},
	computed: {
		/**
		 * @return {Number} sticky column index
		 */
		stickyColumnIndex() {
			return this.columns.findIndex(c => c.title === this.stickyColumn);
		},
		/**
		 * @return {Array} arrray of rows from 'tableData' prop
		 */
		rows() {
			let idx = this.getColumnIndex(this.innerSort);
			if (idx == -1) {
				idx = 0;
			}
			const applySort = (a, b) => {
				const first = a[idx] || "";
				const second = b[idx] || "";

				if (first.valueOf() < second.valueOf()) {
					return this.innerOrder === "asc" ? -1 : 1;
				}
				if (first.valueOf() > second.valueOf()) {
					return this.innerOrder === "asc" ? 1 : -1;
				}
				return 0;
			};
			if (this.pagination && this.paginationType === "static") {
				// console.log("displaying ", this.offset + this.resultsPerPage);

				return this.tableData.slice(0, this.offset + this.resultsPerPage).sort(applySort);
			} else {
				return this.tableData.slice(0).sort(applySort);
			}
		},
		/**
		 * @return {Boolean} true if all results are displayed.(computed based on 'paginationType')
		 */
		noMoreResults() {
			return this.paginationType === "static"
				? this.rows.length === this.tableData.length && this.tableData.length !== 0
				: this.noMoreApiResults;
		}
	},
	watch: {
		/**
		 * watch for tableData update
		 */
		tableData: {
			handler() {
				//hide loader
				this.loadingMore = false;
			},
			deep: true
		}
	},
	mounted() {
		if (this.sort) {
			this.$nextTick(() => {
				/**
				 * sorting column on default sort and order
				 */
				this.sortColumn(this.sort, this.order);
			});
		}

		const tableWrapper = this.$refs["data-table-wrapper"];
		/**
		 * binding scroll event on wrapper
		 */
		tableWrapper.addEventListener("scroll", () => {
			this.loadMore();
		});
	},
	methods: {
		resetWidth(event) {
			const th = event.currentTarget;
			th.style.width = "";
		},
		handleMouseDown(event, n) {
			const elements = this.$refs["data-table"].querySelector("thead tr").children;
			// console.log(elements);
			// console.log(elements.item(n));
			const handle = elements.item(n); //event.currentTarget;
			if (handle) {
				this.resizeMeta.handle = handle;
				this.resizeMeta.pressed = true;
				this.resizeMeta.startX = event.pageX;
				this.resizeMeta.startWidth = handle.offsetWidth;
			}
			// console.log(this.resizeMeta);
		},
		handleMouseUp() {
			// console.log(event);
			if (this.resizeMeta.pressed) {
				this.resizeMeta.pressed = false;
				// this.highlightBorderColumnIdx = -1;
				this.disableClickAfterResize = true;

				setTimeout(() => {
					this.disableClickAfterResize = false;
				}, 100);
			}
		},
		handleMouseMove(event) {
			if (this.resizeMeta.pressed) {
				// console.log("resizeMeta : ", this.resizeMeta);
				const width = this.resizeMeta.startWidth + (event.pageX - this.resizeMeta.startX);
				// console.log("width", width);
				this.resizeMeta.handle.style.width = `${width}px`;
				this.resizeMeta.handle.style.minWidth = `${width}px`;
			}
		},
		handleColumnClick(rowIndex, columnIndex) {
			// console.log("Column clicked ", rowIndex, columnIndex);
			if (!this.disableClickAfterResize) {
				const selectedColumn = this.columns[columnIndex];

				if (this.selectedColumn === columnIndex) {
					this.selectedColumn = -1;
				} else {
					this.selectedColumn = columnIndex;
					/**
					 * columnclick event
					 *
					 * @event columnclick
					 */

					this.$emit("columnclick", rowIndex, columnIndex, selectedColumn);
				}
			}
		},
		handleRowClick(rowIndex, row) {
			if (!this.disableClickAfterResize) {
				/**
				 * rowclick event
				 *
				 * @event rowclick
				 */

				this.$emit("rowclick", rowIndex, row);
			}
		},
		loadMore(ignorescroll = false) {
			const tableWrapper = this.$refs["data-table-wrapper"];

			const currentTop = tableWrapper.scrollTop;

			// console.log("scrolltop old, current ", this.scrollTop, currentTop);
			if (this.scrollTop < currentTop || ignorescroll) {
				this.scrollTop = currentTop;

				if (this.pagination) {
					if (
						tableWrapper.scrollTop + tableWrapper.clientHeight >= tableWrapper.scrollHeight &&
						this.paginationType === "static"
					) {
						if (this.rows.length < this.tableData.length) {
							this.offset += this.resultsPerPage;
						}
					}
					if (
						tableWrapper.scrollTop + tableWrapper.clientHeight >= tableWrapper.scrollHeight &&
						this.paginationType === "api" &&
						!this.loadingMore &&
						!this.noMoreApiResults
					) {
						this.offset += this.resultsPerPage;
						this.loadingMore = true;

						/**
						 * next event triggered when we want to paginate/retrieve next results, that is user can call api with offset and update 'tableData' prop
						 *
						 * @event next
						 */
						this.$emit("next", this.offset);
					}
				}
			}
		},
		/**
		 * @method handleColumnChange
		 * @param {String} oldval old value of column
		 * @param {String} newval new value of column
		 * @param {Boolean} hasError if column has an error
		 * @param {Object} columnObject DataTableColumn object
		 */
		handleColumnChange(oldval, newval, hasError, columnObject) {
			//		console.log(oldval, newval);

			/**
			 * columnchange event
			 *
			 * @event columnchange
			 */
			this.$emit("columnchange", oldval, newval, hasError, columnObject);
		},
		/**
		 * @method getColumnIndex
		 * @param {String} column column title to find index
		 */
		getColumnIndex(column) {
			return this.columns.findIndex(c => c.title === column);
		},
		/**
		 * @method sortColumn
		 * @param {String} column column title to sort
		 * @param {String} order order to sort , that is 'asc' or 'desc'
		 */
		sortColumn(column, order) {
			this.innerSort = column;
			this.innerOrder = order;
			// console.log("Sorting ... ", column, order);
			const idx = this.getColumnIndex(column);
			// eslint-disable-next-line vue/no-mutating-props
			this.tableData.sort((a, b) => {
				const first = a[idx] || "";
				const second = b[idx] || "";

				if (first.valueOf() < second.valueOf()) {
					return order === "asc" ? -1 : 1;
				}
				if (first.valueOf() > second.valueOf()) {
					return order === "asc" ? 1 : -1;
				}
				return 0;
			});
		}
	}
});
</script>
<style lang="scss">
div.flow-data-table {
	width: 100%;
	// height: 100%;
	// max-height: 100vh;
	border-radius: 8px;
	border: 1px solid var(--dashboard-border);
	overflow: auto;

	> table {
		min-width: 100%;

		border-collapse: separate;
		border-spacing: 0;
		> thead {
			tr:first-child th {
				position: sticky;
				top: 0;
				z-index: 2;
			}
		}
		> thead,
		> tbody {
			> tr {
				> td,
				> th {
					// padding: 12px;
					vertical-align: middle;
					text-align: left;
					min-width: 150px;

					* {
						vertical-align: middle;
					}

					&.sticky {
						position: sticky;
						left: 0;
						z-index: 4;
					}
					&.highlight-resize {
						border-right: 2px solid var(--dashboard-primary) !important;
						user-select: none;
					}
				}
				> td {
					height: 1px;
					&[selected] {
						> * {
							position: relative;
							z-index: 1;
							height: 100%;
							* {
								z-index: 3;
							}
							&:after {
								position: absolute;
								content: " ";
								top: 0px;
								right: 0px;
								bottom: 0px;
								left: 0px;
								background-color: var(--dashboard-element);
								opacity: 0.3;
								z-index: 2;
								mix-blend-mode: screen;
							}
						}
					}
				}

				> th {
					color: var(--dashboard-font-light);
					font-size: 12px;
					background-color: var(--dashboard-element-light);
					border-bottom: 1px solid var(--dashboard-border);

					&:not(:last-of-type) {
						border-right: 1px solid var(--dashboard-border);
					}

					&.sticky {
						position: sticky;
						top: 0;
						z-index: 5;
					}

					.sort-icon {
						padding: 16px;
						margin-right: -16px;
						margin-top: -16px;
						margin-bottom: -16px;
						margin-left: auto;
						float: right;
						// align-self: flex-end;
					}
					> div.th-content {
						padding: 12px 0px 12px 0px;

						.th-resizer {
							width: 14px;
							padding: 20px 0px 20px 0px;

							z-index: 6;
							&:first-child {
								margin: -12px 0px -12px -4px;
							}
							&:last-child {
								margin: -12px -4px -12px 0px;
							}
							&:active {
								cursor: col-resize;
							}

							&:hover {
								cursor: col-resize;
								user-select: none;
							}
						}
					}
					&[selected] {
						> div.th-content {
							position: relative;
							z-index: 1;
							* {
								z-index: 3;
							}
							&:after {
								position: absolute;
								content: " ";
								top: 0px;
								right: 0px;
								bottom: 0px;
								left: 0px;
								background-color: var(--dashboard-element);
								opacity: 0.3;
								z-index: 2;
								mix-blend-mode: screen;
							}
						}
					}
					.text-editor {
						display: flex;
						align-items: center;
					}

					// &:active {
					// 	// cursor: col-resize;

					// 	user-select: none;
					// }

					// &:hover {
					// 	user-select: none;
					// }
				}
				> td {
					color: var(--dashboard-font-dark);
					font-size: 12px;
					font-weight: 300;
					cursor: pointer;

					border-bottom: 1px solid var(--dashboard-border);

					&:not(:last-of-type) {
						border-right: 1px solid var(--dashboard-border);
					}
				}

				&:nth-child(odd) {
					> td {
						background-color: var(--dashboard-background);
					}
				}
				&:nth-child(even) {
					> td {
						background-color: var(--dashboard-element-light);
					}
				}
			}
		}

		> thead {
			> tr:first-of-type {
				> th:first-of-type {
					border-top-left-radius: 8px;
				}
				> th:last-of-type {
					border-top-right-radius: 8px;
				}
			}
		}
		> tbody {
			> tr:last-of-type {
				> td:first-of-type {
					border-bottom-left-radius: 8px;
				}
				> td:last-of-type {
					border-bottom-right-radius: 8px;
				}

				> td {
					border-bottom: none;
				}
			}
		}
	}

	div.flow-data-table-no-more-results {
		padding: 12px;
		display: flex;
		width: inherit;
		justify-content: flex-end;

		position: sticky;
		// bottom: 0;
		left: 0;
		// background-color: var(--dashboard-element-light);
	}
	div.flow-data-table-loading-more-results {
		padding: 12px;
		display: flex;
		width: inherit;
		justify-content: flex-end;
		align-items: center;
		position: sticky;
		// bottom: 0;
		left: 0;
		> * + * {
			margin-left: 8px;
		}
	}

	div.flow-data-table-empty {
		padding: 24px;
	}
}
</style>
